"use strict";
'use client';
Object.defineProperty(exports, "__esModule", { value: true });
exports.UseHowlState = void 0;
const react_1 = require("react");
const howler_1 = require("howler");
var UseHowlState;
(function (UseHowlState) {
    UseHowlState[UseHowlState["isLoading"] = 0] = "isLoading";
    UseHowlState[UseHowlState["hasLoaded"] = 1] = "hasLoaded";
    UseHowlState[UseHowlState["isPlaying"] = 2] = "isPlaying";
    UseHowlState[UseHowlState["hasStopped"] = 3] = "hasStopped";
})(UseHowlState || (exports.UseHowlState = UseHowlState = {}));
function useHowl(options) {
    const [urls, setUrls] = (0, react_1.useState)([]);
    const [state, setState] = (0, react_1.useState)(UseHowlState.hasStopped);
    const [error, setError] = (0, react_1.useState)();
    const [totalDuration, setTotalDuration] = (0, react_1.useState)(0);
    const [currentDuration, setCurrentDuration] = (0, react_1.useState)(0);
    const [currentIndex, setCurrentIndex] = (0, react_1.useState)(-1);
    const howlsRef = (0, react_1.useRef)([]);
    const intervalRef = (0, react_1.useRef)(null);
    const loadedCountRef = (0, react_1.useRef)(0);
    (0, react_1.useEffect)(() => {
        stop();
        if (urls.length === 0) {
            return;
        }
        setState(UseHowlState.isLoading);
        howlsRef.current = urls.map((src, index) => {
            return new howler_1.Howl({
                src: [src],
                html5: true,
                onend: () => {
                    if (options === null || options === void 0 ? void 0 : options.onFinishPlayback) {
                        options.onFinishPlayback(index);
                    }
                    if (index + 1 === urls.length && (options === null || options === void 0 ? void 0 : options.onEnd)) {
                        stop();
                        options.onEnd();
                    }
                    else {
                        playNext();
                    }
                },
                onload: () => {
                    loadedCountRef.current += 1;
                    if (loadedCountRef.current === urls.length) {
                        let total = howlsRef.current.reduce((acc, howl) => acc + howl.duration(), 0);
                        // somehow duration from howl is always shorter than actual audio length!
                        total = total * 0.9;
                        setTotalDuration(total);
                        setState(UseHowlState.hasLoaded);
                    }
                },
                onloaderror: (soundId, error) => {
                    console.log(`load error, url ${urls[index]}, error: ${error.message} `);
                    setError(error.message);
                },
                onplayerror: (soundId, error) => {
                    console.log(`playback error, url: ${urls[index]}, error: ${error.message} `);
                    setError(error.message);
                },
            });
        });
        return () => {
            howlsRef.current.forEach(howl => howl.unload());
        };
    }, [urls]);
    const initiate = (0, react_1.useCallback)((urls) => {
        setUrls([...urls]);
    }, []);
    const play = (0, react_1.useCallback)(() => {
        if (howlsRef.current.length === 0) {
            return;
        }
        setState(UseHowlState.isPlaying);
        setCurrentIndex(0);
    }, []);
    const stop = (0, react_1.useCallback)(() => {
        setState(UseHowlState.hasStopped);
        setCurrentDuration(0);
        setCurrentIndex(-1);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (howlsRef.current[currentIndex]) {
            howlsRef.current[currentIndex].stop();
        }
        loadedCountRef.current = 0;
    }, [currentIndex]);
    const playNext = (0, react_1.useCallback)(() => {
        if (currentIndex < howlsRef.current.length - 1) {
            setCurrentIndex(prev => prev + 1);
        }
    }, [currentIndex]);
    const reset = (0, react_1.useCallback)(() => {
        setUrls([]);
        setState(UseHowlState.hasStopped);
        setError(undefined);
        setTotalDuration(0);
        setCurrentDuration(0);
        setCurrentIndex(-1);
        if (howlsRef.current.length > 0) {
            howlsRef.current.map(howl => howl.unload());
            howlsRef.current = [];
        }
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        loadedCountRef.current = 0;
    }, []);
    (0, react_1.useEffect)(() => {
        if (currentIndex < howlsRef.current.length &&
            state === UseHowlState.isPlaying) {
            howlsRef.current[currentIndex].play();
        }
    }, [currentIndex]);
    (0, react_1.useEffect)(() => {
        if (currentIndex !== -1 && state === UseHowlState.isPlaying) {
            intervalRef.current = setInterval(() => {
                const played = howlsRef.current
                    .slice(0, currentIndex)
                    .reduce((acc, howl) => acc + howl.duration(), 0);
                const currentHowl = howlsRef.current[currentIndex];
                const curDuration = played + (currentHowl ? currentHowl.seek() : 0);
                setCurrentDuration(curDuration);
            }, 100);
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [state, currentIndex]);
    return {
        initiate,
        state,
        totalDuration,
        currentDuration,
        play,
        stop,
        error,
        reset,
    };
}
exports.default = useHowl;
